<template>
  <div class="modal"
       :class="{ 'is-active': showModal }">
    <div class="modal-background" />
    <div class="modal-content box">
      <h5 class="title-five mb-3">{{ title ?? $t('fileUpload') }}</h5>
      <div class="progress-container">
        <div class="progress"
             :style=widthStyle />
        <p class="progress-value">{{ percentage ?? 0 }} %</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, PropType, ref, watch } from 'vue'
import { onBeforeRouteLeave, RouteLocationNormalized, useRouter } from 'vue-router'
import { notify } from '@kyvg/vue3-notification'
import i18n from '@/locales'

/*
 * Modal becomes active when percentage is 0-100; it will remain
 * on screen for an additional 750ms. A nav away route guard is
 * active while the modal is active.
 *
 * TIPS
 *  - Use null for initial value
 *  - Use percentage-value 0 before any upload
 *  - Do not reset percentage after each upload
 *
 * Tip #2 is important for fast uploads; percentage can jump to
 * 100 quickly. Tip #3 is necessary to observe the 750ms delay.
 */
const props = defineProps({
  percentage: {
    type: Number as PropType<number | null>,
    default: null
  },
  title: String,
})

const hasDelayedCloseFinished = ref(false)
const router = useRouter()

let delayedRouteNavTo: RouteLocationNormalized|null = null

const showModal = computed(() => {
  return props.percentage !== null &&
    props.percentage <= 100 &&
    props.percentage >= 0 &&
    !hasDelayedCloseFinished.value
})
const widthStyle = computed(() => {
  return { 'width': (props.percentage ?? 0) + '%' }
})

watch(
  () => props.percentage,
  value => {
    if (value === 100) {
      setTimeout(
        () => {
          hasDelayedCloseFinished.value = true
          if (delayedRouteNavTo !== null) {
            router.push(delayedRouteNavTo)
          }
        },
        750
      )
    } else {
      hasDelayedCloseFinished.value = false
    }
  },
  { immediate: true }
)

onBeforeRouteLeave((to) => {
  if (showModal.value) {
    if (delayedRouteNavTo === null) {
      delayedRouteNavTo = to
    } else if (delayedRouteNavTo.fullPath !== to.fullPath && props.percentage === 100) {
      /*
       * If a destination already exists, reject other navigation requests,
       * like from PageCreator to PageEditor.
       */
      return false
    }

    if (props.percentage !== null && props.percentage < 100) {
      notify({
        title: i18n.global.t('itemInProgress', {item: i18n.global.t('upload')}),
        text: i18n.global.t('waitForUpload'),
        type: 'warn',
        duration: 5000
      })
    }

    // reject navigation if modal is active
    return false
  }
})
</script>

<style scoped lang="scss">
.modal-content {
  width: 50% !important;
}

.progress-container {
  background-color: var(--grey--light-color);
  border-radius: 10rem;
  height: 2rem;
  overflow: hidden;
  position: relative;
  width: 100%;
}

.progress {
  background-color: var(--primary);
  border-radius: 10rem 0 0 10rem;
  color: var(--primary-color-invert);
  height: 100%;
  position: relative;
  transition: width 0.25s ease-in-out; /* Smooth transition for width */
}

.progress-value {
  color: var(--primary-color-invert);
  font-size: 1rem;
  font-weight: 700;
  left: 50%;
  line-height: 2rem;
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  white-space: nowrap;
}
</style>
